import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import AssistantMessage from './AssistantMessage';
import Suggestions from './Suggestions';
import WelcomeState from './WelcomeState';
import { useAssistant } from './AssistantContext';
import useStream from './useStream';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { colors } from '~/styles';

const S = {
  ChatArea: styled.div`
    display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
    flex: 1;
    height: 100%;
    overflow: hidden;
    flex-direction: column;
    font-size: 0.875rem;
  `,

  ChatScroll: styled.div`
    flex: 1;
    overflow-y: auto;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar {
      width: 0.875rem;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.grey25};
      border-radius: 2.5rem;
      border: 0.25rem solid transparent;
      background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.grey55};
      background-clip: padding-box;
    }
  `,

  Chat: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: ${({ $constrained }) => ($constrained ? '820px' : 'none')};
    margin: 0 auto;
    min-height: 100%;
  `,

  Messages: styled.div`
    flex: 1;
    padding: 1rem 1rem 1rem 1.875rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  `,

  UserMessage: styled.div`
    max-width: 80%;
    align-self: flex-end;
    background: ${colors.primary10};
    color: ${colors.black};
    padding: 1.5rem 1.25rem;
    border-radius: 0.625rem 0.625rem 0 0.625rem;
    white-space: pre-wrap;
    line-height: 1.5;
  `,

  FooterWrapper: styled.div`
    background: ${colors.white};
    padding: 0.5rem 1.875rem 1.0625rem;
    flex-shrink: 0;
    width: 100%;
    max-width: ${({ $constrained }) => ($constrained ? '820px' : 'none')};
    margin: 0 auto;
  `,

  FooterInput: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,

  PlusButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    min-height: 2rem;
    padding: 0;
    border: 1px solid ${colors.grey10};
    border-radius: 50%;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    flex-shrink: 0;
    font-size: 0.75rem;
    box-sizing: border-box;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.grey55};
    }
  `,

  TextareaWrapper: styled.div`
    flex: 1;
    position: relative;
    display: flex;
  `,

  Textarea: styled.textarea`
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${colors.grey10};
    border-radius: 0.625rem;
    padding: 0.8125rem 2.5rem 0.625rem 0.8125rem;
    outline: none;
    resize: none;
    font-size: 0.875rem;

    &::placeholder {
      font-style: italic;
      color: ${colors.grey40};
    }

    &:focus {
      border-color: ${colors.grey10};
      outline: none;
    }
  `,

  SendButton: styled.button`
    position: absolute;
    right: 0.625rem;
    bottom: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.875rem;
    height: 1.875rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: ${colors.grey5};
    color: ${colors.primary};
    cursor: pointer;
    font-size: 0.6875rem;
    box-sizing: border-box;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.accent};
    }

    &:disabled {
      background: ${colors.grey5};
      color: ${colors.grey25};
      opacity: 1;
      cursor: default;
    }
  `,

  Disclaimer: styled.p`
    font-size: 0.625rem;
    color: ${colors.grey25};
    text-align: center;
    margin-top: 0.8125rem;
  `,
};

export default function Assistant({ model, conversationId, onConversationIdChange, onConversationsChange, hidden }) {
  const api = useApi();
  const toast = useToast();
  const { workspace } = useWorkspace();
  const { profileId, setProfileId, mode, closePanel } = useAssistant();
  const isMounted = useIsMounted();

  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);

  const stream = useStream({ setMessages, setThinking });

  const textareaRef = useRef(null);

  // Reset messages when conversationId changes externally (sidebar selection, new chat)
  const prevConversationIdRef = useRef(undefined);
  useEffect(() => {
    if (conversationId === prevConversationIdRef.current) return;
    prevConversationIdRef.current = conversationId;

    stream.abort();
    setThinking(false);

    if (conversationId === null) {
      setMessages(null);
      textareaRef.current?.focus();
      return;
    }

    // Load conversation messages
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).ai().conversations(conversationId).get();
      if (!isMounted.current) return;
      setMessages(data.messages);
      if (data.assistantProfileId) {
        setProfileId(data.assistantProfileId);
      }
      textareaRef.current?.focus();
    })();
  }, [conversationId, api, workspace.id, isMounted, setProfileId, stream]);

  const handleSend = async (text) => {
    if (thinking) return;
    const userText = text || input.trim();
    if (!userText) return;

    const next = [...(messages ?? []), { role: 'user', parts: [{ type: 'input_text', text: userText }] }];
    next.push({ role: 'assistant', parts: [] });

    setMessages(next);
    setInput('');

    try {
      const baseUrl = `/api/www/workspaces/${workspace.id}/ai/conversations`;
      const payload = { model, text: userText, assistantProfileId: profileId };
      if (!payload.model) delete payload.model;

      if (conversationId) {
        await stream.run(`${baseUrl}/${conversationId}/messages`, payload);
      } else {
        await stream.run(baseUrl, payload, {
          afterComplete: async () => {
            const { data } = await api.www.workspaces(workspace.id).ai().conversations().list();
            onConversationsChange({ data, isReady: true, error: null });

            const newest = data[0];
            const convo = await api.www.workspaces(workspace.id).ai().conversations(newest.id).get();

            prevConversationIdRef.current = newest.id;
            onConversationIdChange(newest.id);
            setMessages(convo.data.messages);
          },
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (!isMounted.current) return;

      toast.error(error.message || 'Sorry, something went wrong. Please try again.');
      setMessages((msgs) => {
        if (!msgs) return msgs;
        return [
          ...msgs,
          {
            role: 'assistant',
            parts: [{ type: 'refusal', text: 'Sorry, something went wrong. Please try again.' }],
          },
        ];
      });
      setThinking(false);
    }
  };

  const lastUserIndex = React.useMemo(() => {
    if (!messages?.length) return -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return i;
    }
    return -1;
  }, [messages]);

  const lastUserAnchorRef = React.useRef(null);

  useEffect(() => {
    lastUserAnchorRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, [lastUserIndex]);

  const handleChatClick = useCallback(
    (e) => {
      if (mode !== 'drawer') return;
      const anchor = e.target.closest('a');
      if (anchor && !anchor.target) closePanel();
    },
    [mode, closePanel],
  );

  const isConstrained = true;
  const showWelcome = !messages || messages.length === 0;
  const showSuggestions = !thinking && !conversationId;

  return (
    <S.ChatArea $hidden={hidden}>
      <S.ChatScroll onClick={handleChatClick}>
        <S.Chat $constrained={isConstrained}>
          {showWelcome ? (
            <WelcomeState />
          ) : (
            <S.Messages key={conversationId}>
              {messages?.map((msg, i) => {
                const isLastUser = i === lastUserIndex;
                const isLast = i === messages.length - 1;
                const isLastAssistant = msg.role === 'assistant' && i === messages.length - 1;

                return (
                  <React.Fragment key={i}>
                    {msg.role === 'user' && (
                      <>
                        {isLastUser && <div ref={lastUserAnchorRef} style={{ scrollMarginTop: '1rem' }} />}
                        <S.UserMessage>
                          {msg.parts.map((part, j) => (
                            <React.Fragment key={j}>{part.text}</React.Fragment>
                          ))}
                        </S.UserMessage>
                      </>
                    )}

                    {msg.role === 'assistant' && (
                      <AssistantMessage
                        message={msg}
                        conversationId={conversationId}
                        isLast={isLast}
                        thinking={isLastAssistant && thinking}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </S.Messages>
          )}
        </S.Chat>
      </S.ChatScroll>

      <S.FooterWrapper $constrained={isConstrained}>
        {showSuggestions && <Suggestions conversationId={conversationId} onSend={handleSend} />}
        <S.FooterInput>
          <S.TextareaWrapper>
            <S.Textarea
              autoFocus
              rows={4}
              placeholder="What can I help you with?"
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!thinking) handleSend();
                }
              }}
            />
            <S.SendButton onClick={() => handleSend()} disabled={thinking || !input.trim()} title="Send">
              <Icon icon="paper-plane-top" />
            </S.SendButton>
          </S.TextareaWrapper>
        </S.FooterInput>
        <S.Disclaimer>
          Ruddr AI may make mistakes.
          <br />
          Data and analysis provided are based on your workspace permissions.
        </S.Disclaimer>
      </S.FooterWrapper>
    </S.ChatArea>
  );
}
